// Share Button Component - Separate from 3-dot menu
import React, { useState } from 'react';
import { Share2 } from 'lucide-react';
import { AudioFile } from '@types';
import { ShareFileModal } from './ShareFileModal';
import { cn } from '@utils/classNames';
import { showSuspensionToastIfNeeded } from '@utils/suspension';
import { useToast } from '@components/ui';

interface ShareButtonProps {
  file: AudioFile;
  onSuccess?: () => void;
  className?: string;
  size?: 'sm' | 'md' | 'lg';
  variant?: 'icon' | 'button';
}

/**
 * Modular Share Button component
 * Separate from the 3-dot menu for better accessibility
 */
export const ShareButton: React.FC<ShareButtonProps> = ({
  file,
  onSuccess,
  className,
  size = 'md',
  variant = 'icon',
}) => {
  const [showShareModal, setShowShareModal] = useState(false);
  const { error: showError } = useToast();

  const sizeClasses = {
    sm: 'w-4 h-4',
    md: 'w-5 h-5',
    lg: 'w-6 h-6',
  };

  const buttonSizeClasses = {
    sm: 'min-w-[28px] min-h-[28px] p-1.5',
    md: 'min-w-[32px] min-h-[32px] p-2',
    lg: 'min-w-[36px] min-h-[36px] p-2.5',
  };

  return (
    <>
      <button
        data-share-button
        onClick={async (e) => {
          e.stopPropagation();
          
          // Check suspension immediately on first click
          const isSuspended = await showSuspensionToastIfNeeded(showError);
          if (isSuspended) {
            return;
          }
          
          setShowShareModal(true);
        }}
        className={cn(
          'flex items-center justify-center rounded-lg transition-all group',
          'hover:bg-primary/10 active:bg-primary/20',
          variant === 'icon' ? buttonSizeClasses[size] : 'px-3 py-2 gap-2',
          className
        )}
        title={file.isShared ? 'File is shared - Click to manage' : 'Share file'}
        aria-label="Share file"
      >
        <Share2 className={cn(
          sizeClasses[size],
          file.isShared ? 'text-primary' : 'text-white/60 group-hover:text-primary'
        )} />
        {variant === 'button' && (
          <span className={cn(
            'font-inter text-sm font-semibold',
            file.isShared ? 'text-primary' : 'text-white'
          )}>Share</span>
        )}
      </button>

      {showShareModal && (
        <ShareFileModal
          file={file}
          onClose={() => {
            setShowShareModal(false);
            onSuccess?.();
          }}
        />
      )}
    </>
  );
};

